import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Lottie from "lottie-react";
import { useCountdown } from "@/hooks/useCountdown";
import { fireRainbowConfetti } from "@/features/tutor/utils/celebrationConfetti";
import { playCelebrationSound } from "@/features/tutor/utils/celebrationSounds";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import celebrationLottie from "@/imports/celebration.lottie.json";
import type { LessonCompleteVariant } from "@/features/tutor/utils/lessonCompletion";

const COUNTDOWN_MS = 5000;

interface LessonCompleteModalProps {
  variant: LessonCompleteVariant;
  onExit: () => void;
}

const RING_SIZE = 28;
const RING_STROKE = 3;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const LessonCompleteModal: React.FC<LessonCompleteModalProps> = ({
  variant,
  onExit,
}) => {
  const isCelebrate = variant === "celebrate";
  const secondsLeft = useCountdown(COUNTDOWN_MS);
  const hasExitedRef = useRef(false);
  const lessonName = useSlideStore((s) => s.currentLessonName);

  const exitOnce = useCallback(() => {
    if (hasExitedRef.current) return;
    hasExitedRef.current = true;
    onExit();
  }, [onExit]);

  // Celebrate on mount: continuous edge confetti + the "yeah" sound.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!isCelebrate) {
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
    playCelebrationSound("lesson");
    const stopConfetti = fireRainbowConfetti(2000);
    return () => {
      stopConfetti();
      document.body.style.overflow = prevOverflow;
    };
  }, [isCelebrate]);

  // Auto-exit when the countdown reaches zero.
  useEffect(() => {
    if (secondsLeft <= 0) exitOnce();
  }, [secondsLeft, exitOnce]);

  const dashOffset =
    RING_CIRCUMFERENCE * (1 - Math.max(secondsLeft, 0) / (COUNTDOWN_MS / 1000));

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lesson-complete-title"
        className="relative bg-white rounded-3xl shadow-2xl w-[90%] max-w-[440px] px-10 pt-12 pb-9 text-center"
      >
        <div className="w-[150px] h-[150px] rounded-full bg-[#FBF3DC] flex items-center justify-center mx-auto mb-7">
          {isCelebrate ? (
            <Lottie
              animationData={celebrationLottie}
              loop
              autoplay
              className="w-[110px] h-[110px]"
              aria-hidden="true"
            />
          ) : (
            <svg
              width="72"
              height="72"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#4FA829"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          )}
        </div>

        <h2
          id="lesson-complete-title"
          className={`text-[30px] leading-tight font-extrabold text-[#2b2b2b] ${
            lessonName ? "mb-5" : "mb-8"
          }`}
          style={{ fontFamily: "Nunito, sans-serif" }}
        >
          {isCelebrate
            ? "Great job completing the lesson"
            : "You're now done with the lesson"}
        </h2>

        {lessonName && (
          <div
            className="inline-block rounded-full bg-[#E8F5E0] text-[#4FA829] font-bold text-[16px] px-5 py-2 mb-8"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            {lessonName}
          </div>
        )}

        <button
          onClick={exitOnce}
          autoFocus
          className="mx-auto flex items-center justify-center gap-3 py-4 px-8 text-[18px] font-extrabold text-white rounded-2xl transition-opacity duration-150 can-hover:hover:opacity-90 active:opacity-80"
          style={{ background: "#8F3230", fontFamily: "Nunito, sans-serif" }}
          aria-label={`End lesson in ${secondsLeft} seconds`}
        >
          <span>End Lesson</span>
          <span className="relative inline-flex items-center justify-center">
            <svg
              width={RING_SIZE}
              height={RING_SIZE}
              viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
              className="-rotate-90"
            >
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke="rgba(255,255,255,0.35)"
                strokeWidth={RING_STROKE}
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke="white"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                style={{ transition: "stroke-dashoffset 1s linear" }}
              />
            </svg>
            <span className="absolute text-[12px] font-bold tabular-nums">
              {secondsLeft}
            </span>
          </span>
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default LessonCompleteModal;
